import type { NavigationMode } from '../App';
import type { Translate } from '../lib/i18n';

interface KeyboardShortcutsProps {
  t: Translate;
  navigationMode: NavigationMode;
  labelsEnabled: boolean;
  onNavigationModeChange: (mode: NavigationMode) => void;
  onClearFocus: () => void;
  onToggleLabels: () => void;
}

interface CommandKey {
  code: string;
  label: string;
  tone?: 'accent' | 'active' | 'muted';
  onClick?: () => void;
}

export function KeyboardShortcuts({
  t,
  navigationMode,
  labelsEnabled,
  onNavigationModeChange,
  onClearFocus,
  onToggleLabels
}: KeyboardShortcutsProps) {
  const fly = navigationMode === 'fly';
  const keys = commandKeys(navigationMode, labelsEnabled, t, onNavigationModeChange, onClearFocus, onToggleLabels);

  return (
    <div className="command-map" aria-label={t('keyboardTitle')}>
      <div className="command-map__head">
        <span>{fly ? t('keyboardFlightControls') : t('keyboardViewControls')}</span>
      </div>
      <div className={fly ? 'command-keyboard is-flight' : 'command-keyboard'}>
        <div className="command-main">
          <div className="command-row">
            <KeySlot code="Q" command={keys.Q} />
            <KeySlot code="W" command={keys.W} />
            <KeySlot code="E" command={keys.E} />
            <KeySlot code="R" command={keys.R} />
          </div>
          <div className="command-row command-row--home">
            <KeySlot code="A" command={keys.A} />
            <KeySlot code="S" command={keys.S} />
            <KeySlot code="D" command={keys.D} />
            <KeySlot code="F" command={keys.F} />
          </div>
          <div className="command-row command-row--bottom">
            <KeySlot code="Shift" command={keys.Shift} wide />
            <KeySlot code="C" command={keys.C} />
          </div>
        </div>
        <div className="command-arrows">
            <KeySlot code={ARROW_UP} command={keys.ArrowUp} compact />
          <div className="command-arrows__row">
            <KeySlot code={ARROW_LEFT} command={keys.ArrowLeft} compact />
            <KeySlot code={ARROW_DOWN} command={keys.ArrowDown} compact />
            <KeySlot code={ARROW_RIGHT} command={keys.ArrowRight} compact />
          </div>
        </div>
      </div>
    </div>
  );
}

function commandKeys(
  navigationMode: NavigationMode,
  labelsEnabled: boolean,
  t: Translate,
  onNavigationModeChange: (mode: NavigationMode) => void,
  onClearFocus: () => void,
  onToggleLabels: () => void
): Partial<Record<string, CommandKey>> {
  const fly = navigationMode === 'fly';

  if (fly) {
    return {
      Q: { code: 'Q', label: t('keyClear'), onClick: onClearFocus },
      W: { code: 'W', label: t('keyForward') },
      E: { code: 'E', label: t('keyUp') },
      R: { code: 'R', label: t('keyLabels'), tone: labelsEnabled ? 'active' : undefined, onClick: onToggleLabels },
      A: { code: 'A', label: t('keyLeft') },
      S: { code: 'S', label: t('keyBack') },
      D: { code: 'D', label: t('keyRight') },
      F: { code: 'F', label: t('keyExitFly'), tone: 'accent', onClick: () => onNavigationModeChange('overview') },
      Shift: { code: 'Shift', label: t('keySprint') },
      C: { code: 'C', label: t('keyDown') },
      ArrowUp: { code: ARROW_UP, label: t('keyLook') },
      ArrowLeft: { code: ARROW_LEFT, label: t('keyLook') },
      ArrowDown: { code: ARROW_DOWN, label: t('keyLook') },
      ArrowRight: { code: ARROW_RIGHT, label: t('keyLook') }
    };
  }

  return {
    Q: { code: 'Q', label: t('keyClear'), onClick: onClearFocus },
    R: { code: 'R', label: t('keyLabels'), tone: labelsEnabled ? 'active' : undefined, onClick: onToggleLabels },
    A: { code: 'A', label: t('overview'), tone: navigationMode === 'overview' ? 'active' : undefined, onClick: () => onNavigationModeChange('overview') },
    S: { code: 'S', label: t('focus'), tone: navigationMode === 'focus' ? 'active' : undefined, onClick: () => onNavigationModeChange('focus') },
    D: { code: 'D', label: t('depth'), tone: navigationMode === 'deep' ? 'active' : undefined, onClick: () => onNavigationModeChange('deep') },
    F: { code: 'F', label: t('fly'), tone: 'accent', onClick: () => onNavigationModeChange('fly') }
  };
}

const ARROW_UP = '\u2191';
const ARROW_LEFT = '\u2190';
const ARROW_DOWN = '\u2193';
const ARROW_RIGHT = '\u2192';

function KeySlot({
  code,
  command,
  wide,
  compact
}: {
  code: string;
  command?: CommandKey;
  wide?: boolean;
  compact?: boolean;
}) {
  const className = [
    'command-key',
    wide ? 'command-key--wide' : '',
    compact ? 'command-key--compact' : '',
    !command ? 'is-empty' : '',
    command?.tone === 'active' ? 'is-active' : '',
    command?.tone === 'accent' ? 'is-accent' : '',
    command?.tone === 'muted' ? 'is-muted' : ''
  ]
    .filter(Boolean)
    .join(' ');

  if (!command) {
    return (
      <div className={className} aria-hidden="true">
        <kbd>{code}</kbd>
      </div>
    );
  }

  const content = (
    <>
      <kbd>{command.code}</kbd>
      <span>{command.label}</span>
    </>
  );

  if (command.onClick) {
    return (
      <button type="button" className={className} onClick={command.onClick} title={command.label}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}
